// Session flow — the one place that decides which auth call happens when.
//
// Three login paths, all ending in a session, all living in this file so they cannot
// drift apart:
//
//   1. first visit            -> create_account
//   2. returning visitor      -> token_login, falling back to create_account only if the
//                               backend actually refused the token
//   3. user-initiated login   -> email/firebase/facebook/apple, handled by the store, whose
//                               publishLogin() calls requireLoginSession() first
//
// None of them ends in a bare `markReady`: a 200 from an auth endpoint says the backend made a
// session, not that this browser kept the cookie for it, nor that the cookie it kept is the one
// that response issued. The two entry points differ in what they may conclude — the bootstrap
// owns the gate and may settle it either way, a login may only ever open it — and neither
// applies a response it has not validated.
//
// Every check here reads a fact at the moment it matters. Nothing predicts whether cookies will
// work later; that was tried and each predictive check needed ordering, scope and lifecycle rules
// of its own, which is where its bugs came from.
//
// `token_login` also slides the 14-day expiry (`UserSession.get_session(...,
// extend_expiry_by_login=True)` on the backend), which is why it still runs for visitors
// whose cookie is perfectly fine — but as a throttled background renewal, never as a
// barrier. See `renewIfDue`.

import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { ApiClientError } from "./client";
import { userApi } from "./user";
import { CLIENT_CONFIG, SESSION_COOKIE_NAME } from "~/config/client";
import { devLog } from "~/lib/dev-log";
import {
  getSessionGeneration,
  markReady,
  markUnavailable,
  setSessionStarter,
  whenSessionSettled,
} from "~/lib/session-gate";

/**
 * How stale a session may get before a background renewal runs.
 *
 * The window being slid is 14 days, so renewing once per page load would be almost
 * entirely wasted work — a visitor who opens ten pages a day would send ten identical
 * refreshes. One a day keeps the window alive for anyone who returns within 14 days, which
 * is the only thing that matters.
 *
 * Skipping a renewal is safe because the 401 recovery path is the backstop: if the cookie
 * dies during a skipped interval, the next request 401s, recovery runs `token_login`, and
 * that call renews too.
 */
const RENEWAL_INTERVAL_MS = 24 * 60 * 60 * 1000;

/** Deliberately a separate key from `user-storage`: absent simply means "renewal is due". */
const RENEWED_AT_KEY = "blidz_session_renewed_at";

/**
 * Is there a session cookie the browser will actually send?
 *
 * This is the *direct* evidence that a request can authenticate, not a proxy for it. The
 * backend sets `blidz_session_id` without `httponly` precisely so the client can see it
 * (`get_cookie_params` in backend/utils.py, and the comment above it), so there is no need
 * to infer session health from a stored user object or a stored token — neither of which
 * says anything about whether the cookie is still alive. Inferring it from persisted user
 * data was the previous approach and it was wrong for a quarter of web visitors.
 *
 * A present cookie can still be expired server-side, which the 401 replay handles. What
 * this rules out is the far more common and far more expensive case: optimistically firing
 * a whole page of requests when we can see there is nothing to authenticate them with.
 */
function hasLiveSessionCookie(): boolean {
  return Cookies.get(SESSION_COOKIE_NAME) !== undefined;
}

/**
 * Is the session cookie the one this auth response just issued?
 *
 * Stronger than `hasLiveSessionCookie`, and the difference is an identity question rather than a
 * pedantic one: an exact comparison answers "does the browser hold *this* session?" rather than
 * merely "does it hold one?".
 *
 * It rests on a CROSS-REPO CONTRACT, checked endpoint by endpoint in backend/user/views.py rather
 * than assumed from one example:
 *
 *   signup, create_account                  `session_id = response_data["content"]["token"]`
 *   finish_delayed_signup, email_login,     `token = session.session_id`, returned as
 *   firebase_login, apple_login,             `"token": token`, and written by
 *   facebook_login, token_login              `write_session_id_to_cookie(resp, request, token)`
 *
 * Session ids are `f"{get_js_time()}-{uuid4()}"` (commonlib/models/user_session.py), so they are
 * digits, hyphens and hex — nothing js-cookie's decode or Django's quoting would alter, which is
 * what makes a byte comparison safe rather than merely usually right.
 *
 * If the backend ever returns a `token` that is not the cookie value, EVERY login through that
 * endpoint fails closed. That is the risk this buys the identity check with; the compensating
 * control is that it fails loudly — `SessionCookieUnavailable`/`session_cookie_not_stored` is its
 * own Sentry group, so a spike there means this contract broke.
 *
 * Which matters most on login. A visitor arrives holding an anonymous session, signs in, and the
 * response's Set-Cookie fails to land: the old anonymous cookie is still there, so "a cookie
 * exists" is true while every later request would authenticate as the wrong identity.
 *
 * Note `token_login` deliberately returns the same id it was given rather than rotating one, so
 * this passes on that healthy path — the test is "matches the issued token", never "changed".
 */
function sessionCookieMatches(issuedToken: string): boolean {
  return Cookies.get(SESSION_COOKIE_NAME) === issuedToken;
}

/**
 * Report a session that cannot exist.
 *
 * These never reach `reportApiFailure` — either no request was made at all, or the request
 * succeeded and the *response* is what we are rejecting — so without this the whole class
 * would be invisible in Sentry.
 *
 * One event per failed session *attempt*, which is the meaningful contrast: the defect this
 * replaced emitted one per blocked request, so a single dead session became ~24 events. It is
 * NOT deduplicated per page load, and deliberately so — a second attempt that also fails is a
 * second fact (a login lifted the gate, a later 401 tried recovery, and that failed too), and
 * suppressing it would hide a genuinely different event to make a comment true. In practice one
 * page load produces one, because a closed gate stops the requests that would trigger another.
 *
 * Fingerprinted on the reason so "cookies don't work here" and "the backend's Set-Cookie did
 * not land" stay separate groups; they have different causes and different fixes.
 */
function reportSessionBlocked(
  reason: "session_cookie_not_stored" | "stale_response_overwrote_cookie",
  via: string
): void {
  const error = new Error(
    reason === "session_cookie_not_stored"
      ? `Session cookie was not stored after ${via} succeeded`
      : `A discarded ${via} response left another session's cookie behind`
  );
  error.name = "SessionCookieUnavailable";

  Sentry.withScope(scope => {
    scope.setFingerprint(["session-cookie-unavailable", reason]);
    scope.setTag("session_blocked_reason", reason);
    scope.setTag("session_blocked_via", via);
    scope.setContext("session", {
      reason,
      via,
      // Recorded because the two failure shapes look different here: no cookie at all, versus a
      // cookie that is present but belongs to an older session — which is the case
      // `sessionCookieMatches` exists to catch, and there this reads "present".
      blidz_session_id: hasLiveSessionCookie() ? "present" : "absent",
      csrftoken: Cookies.get("csrftoken") !== undefined ? "present" : "absent",
    });
    Sentry.captureException(error);
  });
}

/**
 * A discarded auth response may already have changed the cookie.
 *
 * `Set-Cookie` is applied by the browser when the response headers arrive — before the awaited
 * promise resolves — so by the time the generation guard concludes "this result is stale", the jar
 * can already hold the session that result carried. A stale `token_login` reverts
 * `blidz_session_id` to the session it was renewing, while the store holds the one a login just
 * established. The guard protects store state; it cannot un-write a cookie.
 *
 * That outcome is worse than a failure because it SUCCEEDS: requests go out with the old cookie and
 * come back 200 as the wrong identity, so no 401 ever fires and nothing downstream notices.
 *
 * Only the backend can set the right cookie, so there is nothing to repair from here. Stopping is
 * the honest move: `unavailable` blocks the requests that would run as the wrong account, and a
 * reload establishes a session again.
 *
 * NOTE the reload is not requested of the user. Nothing renders `store.error` — `useInitializeUser`
 * returns it and `root.tsx` discards the return value — so on this path the `userMessage` below is
 * dead text and the visible result is a page whose requests fail with no explanation. Sentry is the
 * only consumer. Wiring a banner was considered and deliberately not done: it would also fire for
 * transient bootstrap failures that are silent today.
 *
 * The login path is better but not uniform: `requireLoginSession` throws, the store's catch sets
 * `error` and rethrows, and whether anything is displayed depends on the entry point.
 * `LoginStep`'s own provider buttons and the invitation page render it; `LoginFlow` inside
 * `Login.tsx` is given no `onError`, so there the message is dropped too.
 */
function abandonSessionIfCookieWasClobbered(via: string, expectedToken: string | null): void {
  if (!expectedToken || sessionCookieMatches(expectedToken)) return;

  console.error("❌ A discarded auth response left another session's cookie behind");
  reportSessionBlocked("stale_response_overwrote_cookie", via);
  requireDeps().onSessionFailed(
    new ApiClientError(
      0,
      "SESSION_COOKIE_MISMATCH",
      "A discarded auth response overwrote the session cookie",
      // Unread on this path. Kept because `ApiClientError` carries one by contract and a banner
      // would need it, not because anything shows it today.
      "Something went wrong. Please reload the page."
    )
  );
  markUnavailable();
}

/**
 * Apply a bootstrap auth response — or refuse to, and say why.
 *
 * A 200 from an auth endpoint means the backend made a session. It does not mean this browser
 * kept the cookie for it: when it did not, opening the gate released a whole page of queued
 * requests with nothing to authenticate them — measured in production as a single client turning
 * one page load into 24 CSRF rejections. `unavailable` is the honest verdict, and not a downgrade
 * in behaviour, since those requests were already failing; the difference is one clear Sentry event
 * instead of a burst of them. It is NOT a visible message — see the note in
 * `abandonSessionIfCookieWasClobbered` for why the `userMessage` on this path reaches nobody.
 *
 * Only the bootstrap uses this, because only the bootstrap owns the gate's verdict — see
 * `requireLoginSession` for why a user-initiated login may not close it.
 *
 * Every check happens BEFORE anything is applied, which is the point of routing both bootstrap
 * paths through here. Publishing the user first and validating afterwards left the store holding
 * a populated user, a fresh renewal stamp AND an error, with the gate closed: any component that
 * renders on `user` rather than `error` then showed a signed-in page whose every request failed.
 *
 * Two ways a 200 does not entitle us to open the gate:
 *
 * 1. The session moved on while this call was in flight. A user login uses `session: "none"`, so
 *    it bypasses the gate entirely and can finish while an auth call of ours is still open; if a
 *    stale result were applied afterwards it would overwrite the real user with an anonymous one
 *    and then close the gate on a login that had just succeeded. `renewIfDue` guards this already.
 *
 *    The window worth guarding is a **401 recovery**, not the first bootstrap. Racing
 *    `create_account` would mean logging in during its few hundred ms, and every login here is
 *    behind a click with no redirect-result path that could fire one automatically. A recovery is
 *    different: it runs on an already-rendered page and can take seconds (up to `AUTH_RETRIES`
 *    attempts with 1s then 2s backoff), which is ample time to click "sign in".
 * 2. The cookie is not the one this response issued — see `sessionCookieMatches`.
 */
function acceptBootstrapSession(
  via: "token_login" | "create_account",
  user: unknown,
  issuedToken: string,
  generationAtStart: number
): void {
  const { onSessionEstablished, onSessionFailed, readStoredToken } = requireDeps();

  if (sessionMovedOn(generationAtStart)) {
    abandonSessionIfCookieWasClobbered(via, readStoredToken());
    // Deliberately settles nothing else. Whoever moved the generation on already opened the gate,
    // and the gate's own invariant covers the case where nobody did.
    return;
  }

  if (!sessionCookieMatches(issuedToken)) {
    reportSessionBlocked("session_cookie_not_stored", via);
    // The store is told as well as the gate so the state is coherent, but be clear about what that
    // buys: `store.error` is not rendered anywhere, so this does not explain anything to the user.
    onSessionFailed(
      new ApiClientError(
        0,
        "SESSION_COOKIE_NOT_STORED",
        `${via} succeeded but its session cookie was not stored`,
        // Unread on this path, as above.
        "Something went wrong. Please enable cookies for this site and reload the page."
      )
    );
    markUnavailable();
    return;
  }

  onSessionEstablished(user, issuedToken, via);
  // Only a session that actually exists gets to reset the 14-day renewal clock.
  writeRenewedAt(Date.now());
  markReady();
}

/**
 * Open the gate after a user-initiated login — and never close it.
 *
 * A login is not a bootstrap, so it is not entitled to conclude that no session is possible. If
 * the cookie is missing here the honest response is to report it and leave the gate exactly as it
 * was: a `ready` gate is still backed by whatever session was already working, and an
 * `unavailable` one was not repaired by this login either.
 *
 * Closing it would let a *successful* login break a page that worked moments earlier, and do it
 * silently — the store has already put the user in state, so the UI shows them signed in while
 * every request fails with SESSION_UNAVAILABLE and no error is surfaced. It would also invert the
 * rule the gate is designed around: a login can only ever be good news for it.
 */
export function requireLoginSession(via: string, issuedToken: string): void {
  if (sessionCookieMatches(issuedToken)) {
    markReady();
    return;
  }

  // Throws rather than returning a flag, because every other way a login can fail in this
  // codebase throws: the store's catch sets the error from `userMessage` and rethrows, and the
  // login UI can show it via `onError` — where the entry point wires one; see the note in
  // `abandonSessionIfCookieWasClobbered`. Returning a flag made the six handlers `return undefined`,
  // which is neither "authenticated" nor a rejection — the spinner stopped and the user was told
  // nothing at all.
  //
  // The gate is still deliberately left as it was: a login is not entitled to take a working
  // session away from the page. What must not happen is publishing the login — the jar may still
  // hold a previous, usually anonymous, session, so the UI would show the new identity while
  // every request authenticated as the old one.
  reportSessionBlocked("session_cookie_not_stored", via);
  throw new ApiClientError(
    0,
    "SESSION_COOKIE_NOT_STORED",
    `${via} succeeded but its session cookie was not stored`,
    "Your sign-in could not be saved. Please enable cookies for this site and try again."
  );
}

export interface SessionFlowDeps {
  /** Read the persisted login token. The store owns persistence; this only reads it. */
  readStoredToken: () => string | null;
  /**
   * Re-read the current user from the backend, refreshing anything derived from it.
   *
   * Needed because the optimistic path makes no auth call, and `token_login` used to be
   * what kept the persisted user fresh on every load. Membership drives pricing and the
   * subscription flow is redirect-based, so a stale user shows the pre-subscription price.
   */
  refreshUser: () => Promise<void>;
  /**
   * A session now exists. The store persists the token and updates UI state.
   * `via` exists only because the two paths reported different GTM signup labels before
   * this refactor, and that reporting is preserved verbatim.
   */
  onSessionEstablished: (user: unknown, token: string, via: "token_login" | "create_account") => void;
  /** No session could be established. `status` is 0 for transport failures. */
  onSessionFailed: (error: unknown) => void;
  /** The stored token was refused by the backend and must not be retried. */
  onStoredTokenRejected: () => void;
}

let deps: SessionFlowDeps | null = null;

/**
 * Wire the flow to whoever owns token persistence (the user store), at module init.
 *
 * If the flow ever runs before this is called it throws rather than guessing. Guessing
 * would mean treating a returning visitor as brand new and calling `create_account`,
 * silently replacing their account — a far worse outcome than a loud error.
 */
export function configureSessionFlow(next: SessionFlowDeps): void {
  deps = next;
}

function requireDeps(): SessionFlowDeps {
  if (!deps) {
    throw new Error("session flow used before configureSessionFlow(); check module init order");
  }
  return deps;
}

function readRenewedAt(): number {
  try {
    const raw = localStorage.getItem(RENEWED_AT_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    // Storage can throw in private modes. Treat it as "due" — the safe direction.
    return 0;
  }
}

function renewalIsDue(): boolean {
  return Date.now() - readRenewedAt() >= RENEWAL_INTERVAL_MS;
}

function writeRenewedAt(at: number): void {
  try {
    localStorage.setItem(RENEWED_AT_KEY, String(at));
  } catch {
    // Not being able to record the renewal only costs a redundant refresh next time.
  }
}

/**
 * Did the backend actually refuse this token, as opposed to failing to answer?
 *
 * Only a 4xx is a refusal. That covers every code `token_login` raises — `session_not_found`,
 * `user_id_missing_in_session`, `user_not_allowed_to_log_in`, and the geo codes — so all of
 * them keep today's behaviour of falling back to a new account.
 *
 * Deliberately a status check rather than a list of codes: a code added to the backend
 * later then keeps the existing behaviour instead of silently becoming `unavailable`.
 *
 * A transport failure (status 0) or a 5xx is NOT a refusal. Falling back there is how a
 * network blip discards a working token and manufactures a junk anonymous account — and
 * during a backend outage it would do that to every visitor at once.
 */
function backendRefused(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false;
  return error.status >= 400 && error.status < 500;
}

/**
 * Has a newer session replaced the one this flow was working on?
 *
 * Every exit that settles the gate or touches the store has to ask, not just the successful ones.
 * A user login uses `session: "none"`, completes independently, and bumps the generation — after
 * which this flow's verdict, success OR failure, is about a session nobody is using any more.
 * Applying a failure then takes the gate away from a login that just worked, or wipes it.
 */
function sessionMovedOn(generationAtStart: number): boolean {
  if (getSessionGeneration() === generationAtStart) return false;
  devLog("⏭️ discarding this flow's outcome: the session moved on while it was in flight");
  return true;
}

/**
 * Settle the gate as failed — unless something newer already succeeded.
 *
 * One place rather than a check beside each `markUnavailable`, because the previous shape had the
 * guard on some exits and not others, and the unguarded ones silently broke a completed login.
 */
function failSession(generationAtStart: number, error: unknown): void {
  if (sessionMovedOn(generationAtStart)) return;
  requireDeps().onSessionFailed(error);
  markUnavailable();
}

/**
 * Establish a session. Registered as the gate's starter, so it also serves as the
 * recovery path when a request comes back 401.
 *
 * The caller (the gate) owns the state transitions around this; this function only
 * reports the outcome.
 */
async function establishSession(): Promise<void> {
  // Neither `onSessionEstablished` nor `onSessionFailed` is pulled in here: applying a response is
  // `acceptBootstrapSession`'s job and failing one is `failSession`'s, and both happen only after
  // the generation has been re-checked.
  const { readStoredToken, onStoredTokenRejected } = requireDeps();
  const storedToken = readStoredToken();

  // Captured ONCE, before any await, and used by every branch below. Re-reading it after
  // `token_login` would fold in a generation bump caused by a login that completed during that
  // await, so the guard would compare the post-login value against itself and pass.
  const generationAtStart = getSessionGeneration();

  if (storedToken) {
    try {
      const response = await userApi.tokenLogin(storedToken);
      acceptBootstrapSession("token_login", response.user, response.token, generationAtStart);
      return;
    } catch (error) {
      if (!backendRefused(error)) {
        // Visible in the console on purpose: the request itself is already a Sentry event
        // (ApiClient reports it), but there is deliberately no event for the derived
        // failures, so without this a developer sees a dead page and no local explanation.
        console.error("❌ Session could not be established: token_login gave no verdict", error);
        failSession(generationAtStart, error);
        return;
      }
      // A login that completed while `token_login` was in flight owns the session now. Its token
      // is not the one that was refused, so neither wiping it nor replacing it with a fresh
      // anonymous account is ours to do — `onStoredTokenRejected` clears the store directly, so it
      // has to be guarded here rather than inside `acceptBootstrapSession`.
      if (sessionMovedOn(generationAtStart)) return;
      devLog("🔄 token_login refused by the backend — discarding the token");
      onStoredTokenRejected();
    }
  }

  try {
    const response = await userApi.createAccount({ termsVersion: CLIENT_CONFIG.TERMS_VERSION });
    acceptBootstrapSession("create_account", response.user, response.token, generationAtStart);
  } catch (error) {
    console.error("❌ Session could not be established: create_account failed", error);
    failSession(generationAtStart, error);
  }
}

/**
 * Slide the 14-day expiry, in the background, at most once per `RENEWAL_INTERVAL_MS`.
 *
 * Does NOT close the gate. The session being renewed already works — that is the
 * precondition for being on this path — so nothing issued meanwhile needs protecting, and
 * closing it would give back the round trip this whole design exists to save.
 *
 * A failure here must never create an account: renewal does not mutate identity, only the
 * demand path does. If the token turns out to be dead while the cookie still works, this
 * visit finishes normally and the next cold visit starts a new anonymous account — the
 * alternative would be replacing the user's identity mid-session.
 */
export async function renewIfDue(): Promise<void> {
  const { readStoredToken, onSessionEstablished, onStoredTokenRejected } = requireDeps();

  const storedToken = readStoredToken();
  if (!storedToken) return;
  if (!renewalIsDue()) {
    devLog("⏭️ session renewal not due");
    return;
  }

  // A renewal is slow (up to the full AUTH_RETRIES budget) and runs unblocked, so a user
  // can complete a real login while it is in flight. Applying its result afterwards would
  // overwrite that identity with the anonymous one — and its Set-Cookie would put the
  // anonymous session back, leaving someone who just signed in browsing as a guest.
  const generationAtStart = getSessionGeneration();
  try {
    const response = await userApi.tokenLogin(storedToken);
    if (sessionMovedOn(generationAtStart)) {
      abandonSessionIfCookieWasClobbered("token_login", readStoredToken());
      return;
    }
    // Deliberately not checked against `sessionCookieMatches`, unlike `acceptBootstrapSession`.
    // `token_login` returns the session id it was given rather than minting one
    // (`token = session.session_id`, backend/user/views.py), so a renewal cannot change the cookie
    // value: if its Set-Cookie fails to land, the identical cookie already in the jar still works.
    // Should renewal ever start rotating the id, this needs the same guard the bootstrap has.
    onSessionEstablished(response.user, response.token, "token_login");
    writeRenewedAt(Date.now());
    // Idempotent: the optimistic start already opened the gate, and if the cookie turned
    // out to be dead this call has just revived it. Either way we now have a session.
    markReady();
  } catch (error) {
    if (backendRefused(error) && !sessionMovedOn(generationAtStart)) {
      devLog("🔄 stored token is dead; leaving the live session alone");
      onStoredTokenRejected();
    }
    // Anything else: transient. Try again on the next visit.
  }
}

/**
 * Get the session moving at app start.
 *
 * A returning visitor is treated **optimistically**: the gate opens immediately with no
 * network call at all, because their `blidz_session_id` cookie is probably still valid and
 * the browser attaches it for free. That removes a round trip from the critical path of
 * every repeat visit. If the guess is wrong the first request comes back 401 and the replay
 * path recovers — bounded, automatic, and one re-auth for however many requests were in
 * flight.
 *
 * The optimism is deliberately based on the same thing that authorises the request (a
 * cookie the browser sends itself), not on a stored user object, which says nothing about
 * whether the cookie is still alive. Reading persisted user data and assuming the session
 * behind it is fine was the previous approach, and it was wrong for 25-30% of web visitors.
 *
 * A first-time visitor blocks, because their first paint would otherwise fire a fan-out
 * that is guaranteed to 401 and then replay — twice the requests for no benefit.
 *
 * Note this is only a warm-up: `whenSessionSettled()` self-starts the authoritative flow
 * if this never runs, so skipping it cannot wedge anything.
 */
export function warmUpSession(): void {
  void whenSessionSettled();
}

/**
 * The gate's registered entry point, covering both reasons to run the flow.
 *
 * `authoritative: false` — a first start. A visitor who still has a session cookie
 * short-circuits with no network call at all: the gate opens, the browser attaches the
 * cookie itself, and a round trip comes off the critical path of every repeat visit. If the
 * cookie turns out to be expired server-side, the first request comes back 401 and the
 * replay path recovers — one re-auth for however many requests were in flight.
 *
 * Without a cookie we block instead of guessing. Measured: going optimistic on "a stored
 * token exists" sent 26 doomed requests per page load for visitors whose cookie was gone,
 * and none of them could have succeeded. `hasLiveSessionCookie` is what makes the optimism
 * evidence-based rather than hopeful.
 *
 * `authoritative: true` — recovery after a 401. No shortcut is permitted here.
 *
 * There is deliberately nothing else to consult. Whether a session can be established is decided
 * by trying and looking at the result (`acceptBootstrapSession`), never by predicting the answer
 * first: an earlier revision guessed with a cookie write-probe, and every failure mode it added
 * was a visitor refused a session that would have worked.
 */
async function startSession({ authoritative }: { authoritative: boolean }): Promise<void> {
  if (!authoritative && hasLiveSessionCookie()) {
    markReady();
    // Deliberately NOT awaited, and that is load-bearing rather than a style choice.
    // These calls go through the API client, and a `required` request that comes back 401
    // asks the gate to re-establish — which, from inside this flow, chains onto the flow it
    // is running in and waits for itself. Measured with the follow-up awaited: every request
    // on the page stalled 2s in and never recovered, with no error and no timeout.
    //
    // Detaching also means `inflight` clears as soon as the gate is settled, so a 401
    // arriving moments later starts a real recovery instead of joining a renewal.
    void followUpAfterOptimisticStart();
    return;
  }

  await establishSession();
}

/**
 * The work a returning visitor needs but must not wait for: keep the persisted user fresh,
 * and slide the 14-day window when it is due.
 *
 * Exactly one request either way. `token_login` used to do double duty — establish the
 * session AND return a fresh user — so opening the gate without it silently stopped
 * refreshing membership and accessState, and membership drives pricing behind a
 * redirect-based subscription flow.
 */
async function followUpAfterOptimisticStart(): Promise<void> {
  if (renewalIsDue()) {
    await renewIfDue();
    return;
  }
  try {
    await requireDeps().refreshUser();
  } catch {
    // Rendering from the persisted user is worse than fresh data but far better than a
    // failed page. The next load tries again.
  }
}

setSessionStarter(startSession);
